import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckIcon, ReceiptTextIcon, PackageSearchIcon, AlarmClockIcon, FileClockIcon, CreditCardIcon, BoxIcon } from 'lucide-react';
// The soft warm Giga wash used as the backdrop for the left "Handled" stage,
// so the cycling popups sit on warm brand atmosphere rather than flat cream.
// The image is already a blurred gradient, so it's used near full-bleed with
// only a light veil for card legibility.
const STAGE_TEXTURE = 'https://cdn.magicpatterns.com/uploads/euTogD7KJDSsCDpPEQnDuK/Untitled_design_(6)_1.png';
// The "calm" payoff beat — where TwelveTabProblem piles up cluttered tabs, this
// resolves to a clean, reassuring stack of "Handled" cards. Each card is one
// thing Giga noticed and finished on its own — overdue invoices, a low-stock
// reorder, meeting follow-ups, an expiring contract — so visitors see the
// breadth of proactive work, not just a single scripted demo. The cards cycle
// on a timer over a softly stacked backdrop so it feels like there's always
// another one Giga has quietly taken care of.
type HandledItem = {
  id: string;
  title: string;
  body: string;
  // A topic-appropriate icon for this notification (e.g. an invoice mark for
  // overdue invoices), shown in the card's badge instead of the Giga logo so
  // each card reads as a distinct piece of work at a glance.
  icon: BoxIcon;
  // The connected tools this job touched — shown as small brand chips so the
  // card reads as real cross-tool work, not a generic to-do.
  tools: string[];
  // How long Giga took, for the little footer stat.
  time: string;
};
const HANDLED: HandledItem[] = [{
  id: 'invoices',
  title: 'Found 3 overdue invoices',
  body: 'Drafted and sent follow-up emails to clients.',
  icon: ReceiptTextIcon,
  tools: ['quickbooks', 'gmail'],
  time: 'Done in 12s'
}, {
  id: 'restock',
  title: 'Spotted 5 products running low',
  body: 'Emailed your supplier a reorder and updated the count.',
  icon: PackageSearchIcon,
  tools: ['shopify', 'gmail', 'googlesheets'],
  time: 'Done in 18s'
}, {
  id: 'cold-lead',
  title: 'Spotted a lead going cold',
  body: 'Sent a check-in before the deal went quiet for good.',
  icon: AlarmClockIcon,
  tools: ['hubspot', 'gmail'],
  time: 'Done in 14s'
}, {
  id: 'renewal',
  title: 'Caught a contract expiring Friday',
  body: 'Prepped the renewal and flagged it for your approval.',
  icon: FileClockIcon,
  tools: ['hubspot', 'gmail'],
  time: 'Done in 9s'
}, {
  id: 'auto-charge',
  title: 'Flagged a subscription about to auto-renew',
  body: 'Caught the unused plan before the card got charged again.',
  icon: CreditCardIcon,
  tools: ['stripe', 'gmail'],
  time: 'Done in 8s'
}];
function ToolChip({
  slug
}: {
  slug: string;
}) {
  return <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-[#F4F0EA] border border-black/[0.05] shrink-0" data-id="element-3559">
      <img src={`https://cdn.simpleicons.org/${slug}`} alt="" aria-hidden="true" className="w-3 h-3 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-3560" />
    </span>;
}
// One "Handled" card, styled to match the reference: peach sparkle badge, bold
// title, supporting line, a row of the tools it touched, and a green Handled
// pill with a timing stat.
function HandledCard({
  item
}: {
  item: HandledItem;
}) {
  return (
    // Clean solid card — white surface with a hairline border and a soft
    // shadow, matching the page's established card language (BenefitsBento /
    // GettingStartedSteps). No glass, no photo behind it.
    <div className="relative w-full rounded-[1.75rem] bg-white border border-neutral-200/70 shadow-[0_24px_60px_-30px_rgba(43,33,26,0.30)] p-6 md:p-7 overflow-hidden" data-id="element-3561">
      <div className="relative flex items-start gap-4" data-id="element-3562">
        {/* Topic badge — an orange avatar holding the icon that matches this
          notification's subject (invoice, inventory, calls, contract, report)
          so each card is instantly recognizable. */}
        <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#EA580C] text-white ring-1 ring-white/60 shadow-sm shrink-0" data-id="element-3563">
          <item.icon size={22} strokeWidth={2} aria-hidden="true" data-id="element-3564" />
        </span>
        <div className="flex-1 min-w-0" data-id="element-3565">
          <h3 className="text-lg md:text-xl font-bold text-[#2B211A] leading-snug" data-id="element-3566">
            {item.title}
          </h3>
          <p className="mt-1.5 text-sm md:text-base text-neutral-600 leading-relaxed" data-id="element-3567">
            {item.body}
          </p>
        </div>
      </div>

      {/* Tools touched + Handled pill */}
      <div className="mt-5 flex items-center justify-between gap-3" data-id="element-3568">
        <div className="flex items-center gap-1.5" data-id="element-3569">
          {item.tools.map(slug => <ToolChip key={slug} slug={slug} data-id="element-3570" />)}
        </div>
        <span className="flex items-center gap-1.5 text-sm font-bold text-[#0F9D58] bg-[#0F9D58]/10 rounded-full pl-2.5 pr-3 py-1.5" data-id="element-3571">
          <CheckIcon size={14} strokeWidth={3} data-id="element-3572" />
          Handled
        </span>
      </div>
    </div>
  );
}
// How long each card holds before the next one slides in.
const CYCLE_MS = 3400;
export function WorkYouCantGetTo() {
  const reduced = !!useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % HANDLED.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [reduced]);
  const item = HANDLED[index];
  return <section className="relative w-full overflow-hidden bg-[#F4F0EA] py-24 md:py-32" data-id="element-3573">
      {/* Light-surface backdrop — a single soft warm wash pooled behind the
                                                                              card so the cream stage stays calm but not flat. On desktop the
                                                                              card sits in the left column, so the wash is biased left; on
                                                                              mobile the card is centered and the wide falloff still reads as a
                                                                              gentle pool of warmth. No dot grid, no rings — just one clean wash. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
      background: 'radial-gradient(46% 60% at 32% 48%, rgba(234,88,12,0.06) 0%, rgba(234,88,12,0.02) 38%, transparent 72%)'
    }} data-id="element-3574" />

      <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-3575">
        {/* Visual (Left) — cycling stack of "Handled" cards on the calm cream
          surface. The blurred scenic photo stage was removed so the concrete
          cards are the clear, uncluttered focus. */}
        <div className="relative order-2 md:order-1 flex items-center justify-center min-h-[320px] md:min-h-[520px]" data-id="element-3576">
          {/* Stage backdrop — a zoomed-in, softly-veiled crop of the Giga
           texture inside a rounded panel, so the cycling popups sit on warm,
           vague brand atmosphere instead of flat cream. Desktop only: on a
           narrow phone this large grainy panel reads as a heavy blurred block
           with the small card floating in dead space, so on mobile the card
           sits directly on the clean cream section instead. */}
          <div aria-hidden="true" className="hidden md:block absolute inset-0 rounded-[2.25rem] overflow-hidden border border-neutral-200/60 shadow-[0_30px_70px_-40px_rgba(43,33,26,0.35)]" data-id="element-3577">
            {/* The soft warm wash, near full-bleed. The image is already a
             blurred gradient, so it's used at cover with a gentle zoom for
             depth — no extra blur needed. */}
            <div className="absolute inset-0" style={{
            backgroundImage: `url("${STAGE_TEXTURE}")`,
            backgroundSize: '340%',
            backgroundPosition: '70% 72%',
            backgroundRepeat: 'no-repeat'
          }} data-id="element-3578" />
          </div>
          {/* Foreground — a single cycling card and the progress dots, stacked
           vertically and centered over the full-height image. */}
          <div className="relative z-10 flex flex-col items-center gap-8 py-4 md:py-12" data-id="element-3579">
            {/* A single "Handled" card that animates in and out. No deck behind
             it — each example flips/rises into place on its own. The wrapper
             carries the 3D perspective so the card's enter/exit reads as a
             subtle, engaging tilt rather than a flat fade. */}
            <div className="relative w-[300px] md:w-[380px] min-h-[280px] md:min-h-[252px] flex items-start justify-center" style={{
            perspective: '1200px'
          }} data-id="element-3580">
              <AnimatePresence initial={false} mode="popLayout" data-id="element-3581">
                <motion.div key={item.id} className="absolute inset-x-0 top-0 z-10 will-change-transform [transform-style:preserve-3d]" initial={reduced ? {
                opacity: 0
              } : {
                opacity: 0,
                y: 44,
                scale: 0.9,
                rotateX: 14,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                rotateX: 0,
                filter: 'blur(0px)'
              }} exit={reduced ? {
                opacity: 0
              } : {
                opacity: 0,
                y: -40,
                scale: 0.94,
                rotateX: -12,
                filter: 'blur(8px)'
              }} transition={{
                opacity: {
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1]
                },
                filter: {
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1]
                },
                rotateX: {
                  type: 'spring',
                  stiffness: 220,
                  damping: 26,
                  mass: 0.7
                },
                y: {
                  type: 'spring',
                  stiffness: 240,
                  damping: 24,
                  mass: 0.7
                },
                scale: {
                  type: 'spring',
                  stiffness: 240,
                  damping: 24,
                  mass: 0.7
                }
              }} data-id="element-3582">
                  <HandledCard item={item} data-id="element-3583" />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Progress dots — sit cleanly below the card on the cream surface. */}
            <div className="flex items-center gap-1.5" data-id="element-3584">
              {HANDLED.map((h, i) => <button key={h.id} type="button" aria-label={`Show example: ${h.title}`} onClick={() => setIndex(i)} className={`h-1.5 rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${i === index ? 'w-6 bg-[#EA580C]' : 'w-1.5 bg-[#2B211A]/25 hover:bg-[#2B211A]/40'}`} data-id="element-3585" />)}
            </div>
          </div>
        </div>

        {/* Text (Right) */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative z-10 max-w-xl mx-0 text-left order-1 md:order-2" data-id="element-3586">
          <h2 className="font-title font-medium tracking-tight leading-[1.18] text-4xl sm:text-6xl md:text-7xl text-[#2B211A]" data-id="element-3587">
            Nothing slips through the cracks
          </h2>
          <p className="mt-5 md:mt-6 text-xl md:text-2xl font-semibold text-[#2B211A] leading-snug" data-id="element-3588">
            You didn't ask. Giga noticed.
          </p>
          <p className="mt-4 md:mt-5 text-base md:text-lg text-neutral-500 font-medium leading-relaxed" data-id="element-3589">
            Other AI tools wait for instructions. Giga watches your stack, spots
            the work that needs doing, and quietly handles it — then tells you
            what it took care of.
          </p>
        </motion.div>
      </div>
    </section>;
}